import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_CLOSED,
} from '../../util/types';
import {
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { pickRenderableImages } from '../../util/imagesHelpers';

import {
  AspectRatioWrapper,
  NamedLink,
  AvatarSmall,
  ResponsiveImage,
  IconSpinner,
  IconEdit,
} from '../../components';

import EditListingCardForm from './EditListingCardForm';
import Overlay from './Overlay';

import css from './EditListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    images,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
  } = props;

  const firstImage = images?.[0] || currentListing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix) || k.startsWith("scaled-xlarge") || k.startsWith("default"))
    : [];

  // Render the listing image only if listing images are enabled in the listing type
  return (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  );
};

/**
 * EditListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const EditListingCard = props => {
  const config = useConfiguration();
  const intl = useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    editListingId,
    onOpenEditForm,

    onImageUpload,
    uploadedImages,
    uploadedImagesOrder,
    uploadImageError,

    removedImageIds,
    onRemoveListingImage,

    onUpdateListing,
    updateListingInProgress,
    updateListingError,
    updateListingSuccess,
    updatedListings,
    onCloseListing,
    onOpenListing,
    onApproveListing,

    actionsInProgressListingId,
    hasOpeningError,
    hasClosingError,
    hasApprovingError,

    currentUser,
    onSetUpdatedListing,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const listingImageConfig = config.layout.listingImage;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const updatedImages = updatedListings?.[id]?.images?.filter(img =>
    !removedImageIds.map(rImg => rImg.uuid).includes(img.id.uuid)
  ) || [];

  const images = updatedImages ||
  pickRenderableImages(
    currentListing,
    uploadedImages,
    uploadedImagesOrder,
    removedImageIds
  );   

  const combinedImages = [...updatedImages, ...Object?.values(uploadedImages || [])]

  const handleSaveListing = (values) => {
    const images = values?.images;
    onUpdateListing(id, images, currentUser.id.uuid);
  }

  const isEdit = editListingId === id;
  const fetchErrors = { updateListingError, uploadImageError }

  const state = updatedListings?.[id]?.state || currentListing?.attributes?.state;
  const stateClasses = classNames(
    css.state,
    { [css.stateDraft]: state === LISTING_STATE_DRAFT },
    { [css.statePending]: state === LISTING_STATE_PENDING_APPROVAL },
    { [css.statePublished]: state === LISTING_STATE_PUBLISHED },
    { [css.stateClosed]: state === LISTING_STATE_CLOSED }
  );

  const thisListingInProgress = actionsInProgressListingId && actionsInProgressListingId === id;
  const hasError = hasOpeningError || hasClosingError || hasApprovingError;

  const onEditListing = () => {
    onOpenEditForm(!isEdit ? id : null);
    onSetUpdatedListing({
      [id]: {
        images: currentListing?.images,
        state
      }
    })
  }

  return (
    <div className={classes}>

      {thisListingInProgress ? (
        <Overlay>
          <IconSpinner />
        </Overlay>
      ) : hasError ? (
        <Overlay errorMessage={intl.formatMessage({ id: 'ManageListingCard.actionFailed' })} />
      ) : null}

      <div className={css.topContent}>
        <ListingCardImage
          renderSizes={renderSizes}
          title={title}
          currentListing={currentListing}
          images={images}
          config={config}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          style={cardStyle}
          showListingImage={showListingImage}
        />

        <div className={css.info}>
          <div className={css.mainInfo}>
            {showListingImage && (
              <div className={css.title}>
                {richText(title, {
                  longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                  longWordClass: css.longWord,
                })}
              </div>
            )}
            <div className={css.infoItem}>
              <AvatarSmall user={author} className={css.providerAvatar} />
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>

            <div className={css.infoItem}>
              <FormattedMessage id="EditListingsPage.id" values={{ id: <span>{id}</span> }} />
            </div>
            <div className={css.infoItem}>
              <FormattedMessage id="EditListingsPage.state" values={{ state: <span className={stateClasses}>{state}</span> }} />

              {(() => {
                const actionMap = {
                  [LISTING_STATE_PUBLISHED]: { labelId: "EditListingsPage.closeListing", handler: () => onCloseListing(currentListing.id.uuid, currentUser.id.uuid) },
                  [LISTING_STATE_CLOSED]: { labelId: "EditListingsPage.openListing", handler: () => onOpenListing(currentListing.id.uuid, currentUser.id.uuid) },
                  [LISTING_STATE_PENDING_APPROVAL]: { labelId: "EditListingsPage.approveListing", handler: () => onApproveListing(currentListing.id.uuid, currentUser.id.uuid) },
                };

                const action = actionMap[state];
                if (!action) return null;

                return (
                  <span onClick={action.handler} className={css.infoItemButton}>
                    <IconEdit />
                    <FormattedMessage id={action.labelId} />
                  </span>
                );
              })()}
            </div>
          </div>
        </div>

        <div className={css.buttons}>
          {state === LISTING_STATE_DRAFT ? (
            <div
              className={classNames(css.viewButton, css.viewButtonDisabled)}
            >
              <FormattedMessage id="EditListingsPage.viewListing" />
            </div>
          ) : (
            <NamedLink
              className={css.viewButton}
              name="ListingPage" params={{ id, slug }}
            >
              <FormattedMessage id="EditListingsPage.viewListing" />
            </NamedLink>
          )}
          <div
            className={css.editButton}
            type="button"
            onClick={() => onEditListing()}
          >
            <FormattedMessage id={isEdit ? "EditListingsPage.close" : "EditListingsPage.editImages"} />
          </div>
        </div>
      </div>

      {isEdit && (
        <div className={css.bottomContent}>
          <EditListingCardForm
            currentUserId={currentUser.id.uuid}
            onSubmit={handleSaveListing}
            listingImageConfig={listingImageConfig}
            initialValues={{ images: combinedImages || [] }}
            onImageUpload={onImageUpload}
            onRemoveImage={onRemoveListingImage}
            listingId={id}
            slug={slug}
            updateInProgress={updateListingInProgress}
            ready={updateListingSuccess}
            fetchErrors={fetchErrors}
            onUpdateListing={onUpdateListing}
          />
        </div>
      )}
    </div>
  );
};

export default EditListingCard;
